import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { css } from "@/styled-system/css";
import { Box } from "@zillow/constellation-10";
import { useState, useRef, useMemo, useEffect } from "react";
import { STICKY_NAV_HEIGHT_PX, STICKY_SIDEBAR_TOP_PX } from "../../constants/stickyNav.esm.js";
import { parseSubnavItems } from "../../utils/parseSubnavItems.esm.js";
import B2BFooter from "../B2BFooter/B2BFooter.esm.js";
import B2BMainNav from "../B2BMainNav/B2BMainNav.esm.js";
import { getMobileMenuTopPx } from "../B2BMainNav/B2BMainNav.utils.esm.js";
import B2BSubnav from "../B2BSubnav/B2BSubnav.esm.js";
import B2BUserNav from "../B2BUserNav/B2BUserNav.esm.js";
import ContactSalesModal from "../ContactSalesModal/ContactSalesModal.esm.js";
const B2B_HEADER_ID = "b2b-layout-header";
const headerBase = css.raw({
  margin: "0 auto",
  position: "relative"
});
const headerTransparent = css.raw({
  position: "absolute",
  width: "100%",
  left: 0,
  zIndex: "modal"
});
const mainNavSlot = css.raw({
  xl: {
    '&[data-sticky="true"]': {
      minHeight: "var(--b2b-main-nav-height)"
    }
  },
  xlDown: {
    '&[data-mobile-menu="true"]': {
      minHeight: "var(--b2b-mobile-nav-height)"
    }
  }
});
const mainNavSlotStyle = {
  "--b2b-main-nav-height": `${STICKY_NAV_HEIGHT_PX}px`
};
const HIDE_HEADER_SCRIPT = `
(function(){
  var p = new URLSearchParams(window.location.search);
  if(p.has('fromApp')){
    var style = document.createElement('style');
    style.id = 'hide-b2b-header-style';
    style.textContent = '#${B2B_HEADER_ID}{display:none!important}';
    document.head.appendChild(style);
  }
})();
`;
const B2BLayout = (props) => {
  const {
    children,
    env = "prod"
  } = props;
  const [mobileMenu, setMobileMenu] = useState(false);
  const [subnavOpen, setSubnavOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const mainNavSlotRef = useRef(null);
  const headerRef = useRef(null);
  const [mobileMenuLayout, setMobileMenuLayout] = useState();
  const parsedSubnavItems = useMemo(() => {
    if (props.subnavItemsParsed) {
      return props.subnavItemsParsed;
    }
    if (props.subnavItems) {
      return parseSubnavItems(props.subnavItems);
    }
    return void 0;
  }, [props.subnavItems, props.subnavItemsParsed]);
  const headerCss = useMemo(() => ({
    ...headerBase,
    ...props.transparent && headerTransparent
  }), [props.transparent]);
  useEffect(() => {
    if (!props.sticky) {
      return;
    }
    const onScroll = () => {
      const slot = mainNavSlotRef.current;
      if (!slot) {
        return;
      }
      const {
        top,
        bottom
      } = slot.getBoundingClientRect();
      setIsSticky((wasSticky) => wasSticky ? top < 0 : bottom <= 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, [props.sticky]);
  const handleSetMobileMenu = (isOpen) => {
    const nextOpen = typeof isOpen === "function" ? isOpen(mobileMenu) : isOpen;
    setMobileMenu(nextOpen);
    if (!nextOpen) {
      return;
    }
    setSubnavOpen(false);
    if (headerRef.current && mainNavSlotRef.current) {
      setMobileMenuLayout({
        topPx: getMobileMenuTopPx(headerRef.current.getBoundingClientRect().top),
        navHeightPx: mainNavSlotRef.current.getBoundingClientRect().height
      });
    }
  };
  const slotStyle = useMemo(() => ({
    ...mainNavSlotStyle,
    ...mobileMenuLayout && {
      "--b2b-mobile-nav-height": `${mobileMenuLayout.navHeightPx}px`
    }
  }), [mobileMenuLayout]);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("script", { dangerouslySetInnerHTML: {
      __html: HIDE_HEADER_SCRIPT
    } }),
    props.sticky && /* @__PURE__ */ jsx("style", { dangerouslySetInnerHTML: {
      __html: `
                            html {
                                scroll-padding-top: ${STICKY_SIDEBAR_TOP_PX}px;
                            }
                            @keyframes b2bMainNavSlideIn {
                                from { transform: translateY(-100%); }
                                to { transform: translateY(0); }
                            }
                        `
    } }),
    /* @__PURE__ */ jsxs(Box, { id: B2B_HEADER_ID, ref: headerRef, css: headerCss, children: [
      !props.disabled && /* @__PURE__ */ jsx(B2BUserNav, { redirectParam: props.signInRedirectParam, router: props.router, transparent: props.transparent && !mobileMenu, env, signInLink: props.signInLink, userMenuItems: props.userMenuItems }),
      /* @__PURE__ */ jsx(Box, { ref: mainNavSlotRef, "data-sticky": isSticky ? "true" : "false", "data-mobile-menu": mobileMenu ? "true" : "false", css: mainNavSlot, style: slotStyle, children: /* @__PURE__ */ jsx(B2BMainNav, { logo: props.logo, disabled: props.disabled, transparent: props.transparent && !mobileMenu && !isSticky, sticky: isSticky, menu: props.menu, mobileMenu, mobileMenuTopPx: mobileMenuLayout == null ? void 0 : mobileMenuLayout.topPx, setMobileMenu: handleSetMobileMenu, primaryCTALabel: props.primaryCTALabel, primaryCTALink: props.primaryCTALink, secondaryCTALabel: props.secondaryCTALabel, secondaryCTALink: props.secondaryCTALink, secondaryCTAModal: props.secondaryCTAModal }) }),
      !props.transparent && (props.submenu || props.subnavPrimaryItem || parsedSubnavItems) && /* @__PURE__ */ jsx(B2BSubnav, { menu: props.submenu, primaryItem: props.subnavPrimaryItem, items: parsedSubnavItems, searchPlaceholder: props.searchPlaceholder, searchPrefix: props.searchPrefix, isOpen: subnavOpen, onOpenChange: (isOpen) => {
        setSubnavOpen(isOpen);
        if (isOpen) {
          setMobileMenu(false);
        }
      } })
    ] }),
    children,
    /* @__PURE__ */ jsx(ContactSalesModal, {}),
    /* @__PURE__ */ jsx(B2BFooter, { disabled: props.disabled, columns: props.footerColumns, logo: props.logo })
  ] });
};
export {
  B2BLayout as default
};
